// File: frontend/src/context/GlobalContext.js
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { routerService } from "../services/api";
import { getAccountIdentity, getAuthEpoch, isCurrentEpoch } from "../utils/accountScope";
import { deferIdle } from "../utils/deferIdle";

const GlobalContext = createContext(null);

// Local (not UTC) YYYY-MM-DD so ranges match the admin's calendar day.
const toLocalISODate = (date = new Date()) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// Dynamic presets recompute from "today"; "custom" is never auto-changed.
const VALID_PRESETS = ["today", "last_7_days", "last_30_days", "last_90_days", "this_month", "custom"];
const DEFAULT_PRESET = "this_month";

// Returns { from, to } as local ISO dates for a dynamic preset, or null for "custom".
const getRangeForPreset = (preset) => {
  const now = new Date();
  const today = toLocalISODate(now);
  const minusDays = (n) => {
    const s = new Date(now);
    s.setDate(s.getDate() - n);
    return toLocalISODate(s);
  };
  switch (preset) {
    case "today":        return { from: today, to: today };
    case "last_7_days":  return { from: minusDays(6),  to: today };
    case "last_30_days": return { from: minusDays(29), to: today };
    case "last_90_days": return { from: minusDays(89), to: today };
    case "this_month":   return { from: toLocalISODate(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    default:             return null; // custom — do not calculate
  }
};

export function GlobalProvider({ children }) {
  // Resolve the initial preset from localStorage. Missing/invalid → this_month
  // (existing product default). Backward compatible: users with only
  // global_from/global_to and no preset are treated as this_month.
  const initialPreset = (() => {
    const stored = localStorage.getItem("global_date_preset");
    return VALID_PRESETS.includes(stored) ? stored : DEFAULT_PRESET;
  })();

  // Dynamic presets always recalculate fresh on load (so month-to-date, "today",
  // etc. are correct even if the app was last opened yesterday). Custom uses the
  // stored dates.
  const initialRange = (() => {
    if (initialPreset === "custom") {
      const defaults = getRangeForPreset(DEFAULT_PRESET);
      return {
        from: localStorage.getItem("global_from") || defaults.from,
        to:   localStorage.getItem("global_to")   || defaults.to,
      };
    }
    return getRangeForPreset(initialPreset);
  })();

  const [from, setFromState] = useState(initialRange.from);
  const [to,   setToState]   = useState(initialRange.to);
  const [dateRangePreset, setDateRangePresetState] = useState(initialPreset);
  const [selectedRouterId, setSelectedRouterIdState] = useState(() => localStorage.getItem("selectedRouterId") || "");

  // ── The selected router's NAME, published once by whoever holds the list ───
  // Every page is router-scoped, so every page wants to caption itself with the
  // station it is showing. Before this, each page that wanted the name fetched
  // /api/routers for itself — the Sidebar already polls that list every 30 s, so
  // Sales was issuing a second identical request on every load purely to turn an
  // id into a string.
  //
  // The id remains the single source of truth for SCOPE; this is a display
  // label and nothing may make a data decision from it. It is cached so a
  // reload paints the right caption before the router list has come back, and
  // it is cleared with the id on account change — a stale station name from the
  // previous tenant is exactly the kind of leak the hydration guard exists to
  // stop.
  const [selectedRouterName, setSelectedRouterNameState] = useState(
    () => localStorage.getItem("selectedRouterName") || ""
  );

  const setSelectedRouterName = useCallback((name) => {
    const next = name ? String(name) : "";
    setSelectedRouterNameState((prev) => {
      if (prev === next) return prev;          // no-op: this is called from a render-driven effect
      if (next) localStorage.setItem("selectedRouterName", next);
      else localStorage.removeItem("selectedRouterName");
      return next;
    });
  }, []);

  // A simple "signal" that forces pages to reload their data
  const [refreshTick, setRefreshTick] = useState(0);

  // For spinning animation / "alive" feel
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Topbar notification slot. A page that owns alerts (currently the Dashboard)
  // registers { count, onOpen } here so the SINGLE bell in the global Topbar can
  // show its badge and open its panel. null = no page registered → no bell.
  const [topbarNotifications, setTopbarNotificationsState] = useState(null);
  const setTopbarNotifications = useCallback((v) => setTopbarNotificationsState(v || null), []);

  // Tracks the local calendar day we last computed against, so we can detect a
  // day change after the tab was hidden/asleep.
  const lastDayRef = useRef(toLocalISODate());

  // ── Persistence ──────────────────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem("global_from", from);
    localStorage.setItem("global_to",   to);
  }, [from, to]);

  useEffect(() => {
    localStorage.setItem("global_date_preset", dateRangePreset);
  }, [dateRangePreset]);

  // ── Public setters ───────────────────────────────────────────────────────
  // Manual From/To edits switch the range to "custom" so it is never
  // auto-overwritten at midnight.
  const setFrom = useCallback((v) => {
    setFromState((prev) => (typeof v === "function" ? v(prev) : v));
    setDateRangePresetState("custom");
  }, []);

  const setTo = useCallback((v) => {
    setToState((prev) => (typeof v === "function" ? v(prev) : v));
    setDateRangePresetState("custom");
  }, []);

  const setDateRangePreset = useCallback((preset) => {
    setDateRangePresetState(VALID_PRESETS.includes(preset) ? preset : "custom");
  }, []);

  // Apply a preset: set the preset and, for dynamic presets, recalculate dates.
  const applyDatePreset = useCallback((preset) => {
    const next = VALID_PRESETS.includes(preset) ? preset : "custom";
    setDateRangePresetState(next);
    const r = getRangeForPreset(next);
    if (r) {
      setFromState(r.from);
      setToState(r.to);
      lastDayRef.current = toLocalISODate();
    }
  }, []);

  const resetDateRangeToThisMonth = useCallback(() => {
    const nextRange = getRangeForPreset("this_month");
    setDateRangePresetState("this_month");
    setFromState(nextRange.from);
    setToState(nextRange.to);
    localStorage.setItem("global_from", nextRange.from);
    localStorage.setItem("global_to", nextRange.to);
    localStorage.setItem("global_date_preset", "this_month");
    lastDayRef.current = toLocalISODate();
    setRefreshTick((t) => t + 1);
  }, []);

  // Tracks whether the user has actively changed the selection this session, so
  // the async backend hydrate below never clobbers a fresh in-session choice.
  const userTouchedRouterRef = useRef(false);

  const setSelectedRouterId = useCallback((id) => {
    userTouchedRouterRef.current = true;
    setSelectedRouterIdState((prev) => {
      const raw = typeof id === "function" ? id(prev) : id;
      const next = raw ? String(raw) : "";
      if (next) localStorage.setItem("selectedRouterId", next);
      else localStorage.removeItem("selectedRouterId");
      // Best-effort cross-device persistence. localStorage (above) remains the
      // same-device cache/fallback if the backend write fails or is unavailable.
      //
      // Deferred to idle (Phase 5): the Sidebar resolves the selection during
      // boot, so this PUT landed in the dashboard's first request wave and was
      // measured taking 1,545 ms there — competing with the requests the screen
      // is actually waiting for, while nothing waits for this one. The write
      // still happens: deferIdle flushes on pagehide, and the same-device value
      // is already committed to localStorage on the line above, so even a lost
      // PUT degrades to "other devices learn on the next change", never to a
      // wrong selection here.
      if (localStorage.getItem("token") && typeof routerService.setLastSelectedRouter === "function") {
        const value = next ? Number(next) : null;
        deferIdle(() => { routerService.setLastSelectedRouter(value).catch(() => {}); });
      }
      return next;
    });
  }, []);

  // ── Account-scoped router hydration ───────────────────────────────────────
  // THIS PROVIDER LIVES ABOVE <Router> AND NEVER REMOUNTS. Logging out and into
  // another account is an SPA navigation, so without the reset below Account B
  // inherited Account A's selectedRouterId — which is exactly what made the
  // dashboard cache key collide across accounts.
  //
  // The hydrate is therefore keyed to the signed-in identity, not to mount:
  // whenever tenant/user changes we drop the previous account's selection,
  // re-arm the hydration guard, and adopt only a router the NEW account owns.
  const routerHydratedRef = useRef(false);
  const identityRef = useRef(null);

  const hydrateForAccount = useCallback(() => {
    const { tenantId, userId } = getAccountIdentity();
    const identity = tenantId && userId ? `${tenantId}:${userId}` : null;
    if (identity === identityRef.current) return;   // same account — nothing to do

    const previous = identityRef.current;
    identityRef.current = identity;

    // Signed out, or the account changed: the previous account's selection is
    // not ours to keep. Clear state AND storage before anything can read it.
    if (previous !== null || identity === null) {
      userTouchedRouterRef.current = false;
      routerHydratedRef.current = false;
      setSelectedRouterIdState("");
      setSelectedRouterNameState("");
      try {
        localStorage.removeItem("selectedRouterId");
        // The caption follows the scope. A previous tenant's station name left
        // on screen is a cross-account leak even though it drives no query.
        localStorage.removeItem("selectedRouterName");
      } catch (e) {}
    }
    if (!identity) return;                          // signed out — stop here

    if (routerHydratedRef.current) return;
    routerHydratedRef.current = true;
    if (!localStorage.getItem("token")) return;
    if (typeof routerService.getLastSelectedRouter !== "function") return;

    const startEpoch = getAuthEpoch();
    routerService
      .getLastSelectedRouter()
      .then((res) => {
        // Drop a reply that belongs to a previous account, and never clobber a
        // choice the customer made in the meantime.
        if (!isCurrentEpoch(startEpoch)) return;
        if (identityRef.current !== identity) return;
        if (userTouchedRouterRef.current) return;
        const rid = res?.router_id;
        // The backend resolves last_selected_router_id for the AUTHENTICATED
        // user, so anything it returns is owned by this tenant by construction.
        // A null/absent value leaves the selection empty rather than reusing
        // whatever was there before.
        if (rid != null) {
          setSelectedRouterIdState(String(rid));
          try { localStorage.setItem("selectedRouterId", String(rid)); } catch (e) {}
        }
      })
      .catch(() => {/* non-fatal: the selection simply stays empty */});
  }, []);

  // Run on mount and on every auth change (login, logout, another tab's login).
  useEffect(() => {
    hydrateForAccount();
    const onAuthChanged = () => hydrateForAccount();
    const onStorage = (e) => { if (!e.key || e.key === "token" || e.key === "user") hydrateForAccount(); };
    window.addEventListener("pesafii:auth-changed", onAuthChanged);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pesafii:auth-changed", onAuthChanged);
      window.removeEventListener("storage", onStorage);
    };
  }, [hydrateForAccount]);

  // One tick per pulse: while the short refresh pulse is active, repeated clicks
  // are no-ops so a rapid tap burst can't fan out into a request storm.
  const refreshPulseRef = useRef(false);
  const refresh = useCallback(() => {
    if (refreshPulseRef.current) return;
    refreshPulseRef.current = true;
    setIsRefreshing(true);
    setRefreshTick((t) => t + 1);
    window.setTimeout(() => {
      refreshPulseRef.current = false;
      setIsRefreshing(false);
    }, 900);
  }, []);

  // ── Auto-advance dynamic ranges at local midnight & on tab focus ──────────
  useEffect(() => {
    let timerId;

    // Recompute the current dynamic preset and signal pages to reload. No-op for
    // custom ranges.
    const recomputeIfDynamic = () => {
      lastDayRef.current = toLocalISODate();
      if (dateRangePreset === "custom") return;
      const r = getRangeForPreset(dateRangePreset);
      if (!r) return;
      setFromState(r.from);
      setToState(r.to);
      setRefreshTick((t) => t + 1);
    };

    // Precise, drift-free scheduling: time to the next local midnight (+2s pad),
    // then reschedule — never a blind 24h interval.
    const scheduleNext = () => {
      const now = new Date();
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 2, 0);
      const ms = Math.max(1000, nextMidnight.getTime() - now.getTime());
      timerId = window.setTimeout(() => {
        recomputeIfDynamic();
        scheduleNext();
      }, ms);
    };

    // Browser sleep / background tabs may miss the timer — re-check on wake.
    const onWake = () => {
      if (document.visibilityState === "hidden") return;
      if (toLocalISODate() !== lastDayRef.current) recomputeIfDynamic();
    };

    scheduleNext();
    document.addEventListener("visibilitychange", onWake);
    window.addEventListener("focus", onWake);

    return () => {
      window.clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onWake);
      window.removeEventListener("focus", onWake);
    };
  }, [dateRangePreset]);

  const value = useMemo(
    () => ({
      from,
      to,
      setFrom,
      setTo,
      dateRangePreset,
      setDateRangePreset,
      applyDatePreset,
      resetDateRangeToThisMonth,
      selectedRouterId,
      setSelectedRouterId,
      selectedRouterName,
      setSelectedRouterName,
      refreshTick,
      refresh,
      isRefreshing,
      topbarNotifications,
      setTopbarNotifications,
    }),
    [from, to, setFrom, setTo, dateRangePreset, setDateRangePreset, applyDatePreset, resetDateRangeToThisMonth, selectedRouterId, setSelectedRouterId, selectedRouterName, setSelectedRouterName, refreshTick, refresh, isRefreshing, topbarNotifications, setTopbarNotifications]
  );

  return <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>;
}

export function useGlobal() {
  const ctx = useContext(GlobalContext);
  if (!ctx) throw new Error("useGlobal must be used inside <GlobalProvider />");
  return ctx;
}

// Convenience hook for router-scoped pages: the single source of truth for the
// selected router. Thin wrapper over useGlobal() — does NOT introduce parallel
// state.
export function useSelectedRouter() {
  const { selectedRouterId, setSelectedRouterId } = useGlobal();
  return { selectedRouterId, setSelectedRouterId };
}
